import { Trans, useTranslation } from 'react-i18next';

import type {
  MeetingCreatedNotificationData,
  Notification,
} from '@/api/v4/notifications.api';
import { queryClient, QueryKey } from '@/config/query-client';
import { useActionPanelSearchParams } from '@/features/action-panel/use-action-panel-search-params';
import { useNavigateTo } from '@/routes/hooks/use-navigate-to';
import { colors } from '@/theme/colors';
import { Icon } from '@/ui/icons/icon';
import { Inline } from '@/ui/line/line';
import { SmallText } from '@/ui/typography/widgets';

import {
  NotificationCardContainer,
  NotificationHighlight,
  NotificationIconWrapper,
} from '../components/notification-card-components';
import { useCommentTagNotificationEntityName } from '../hooks/use-comment-tag-notification-entity-name.query';
import { useMarkNotificationAsRead } from '../hooks/use-mark-notification-as-read.mutation';
import { timeAgo } from '../utils/time-ago';

export const MeetingCreatedNotificationCard = ({
  notification,
}: {
  notification: Notification;
}) => {
  useTranslation('notification');
  const { data } = notification;
  const { markAsRead } = useMarkNotificationAsRead();
  const isRead = Boolean(notification?.readAt);
  const navigateTo = useNavigateTo();
  const { generateActionItemSearchParam } = useActionPanelSearchParams();

  const { meetingId, organizationId } =
    (data as MeetingCreatedNotificationData | null) ?? {};

  const companyName = useCommentTagNotificationEntityName({
    entityType: 'company',
    entityId: organizationId ?? '',
  });

  if (!data) return null;

  const handleNavigateToMeeting = async () => {
    if (!isRead) {
      await markAsRead({ notificationId: notification.id });
    }

    if (!meetingId || !organizationId) return;

    const meetingSearchParams = generateActionItemSearchParam(
      meetingId,
      'meeting',
    );

    await queryClient.invalidateQueries({
      queryKey: [QueryKey.CompanyMeetings, organizationId],
    });

    return navigateTo.company(
      { companyId: organizationId },
      meetingSearchParams,
    );
  };

  return (
    <NotificationCardContainer
      isRead={isRead}
      justify="space-between"
      alignItems="center"
      onClick={handleNavigateToMeeting}
    >
      <Inline gap="12px" alignItems="center">
        <NotificationIconWrapper
          isRead={isRead}
          alignItems="center"
          justify="center"
        >
          <Icon icon="DocFile" color="white" centered={false} />
        </NotificationIconWrapper>
        <SmallText color={colors.gray.c13}>
          <Trans
            i18nKey="meetingCreated"
            ns="notification"
            values={{ companyName: companyName ?? '' }}
            components={{ strong: <NotificationHighlight /> }}
          />
        </SmallText>
      </Inline>
      <SmallText>{timeAgo(notification?.createdAt)}</SmallText>
    </NotificationCardContainer>
  );
};
